import { useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type { Contract, Award, Bid, CrawlRun, OcrTask, OcrResult, Attachment } from '@/lib/pocketbase'

// Contracts
export function useContracts(filters?: {
  search?: string
  page?: number
  perPage?: number
}) {
  const { search, page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['contracts', filters],
    queryFn: async () => {
      let filter = ''
      if (search) {
        filter = `contract_no ~ "${search}"`
      }

      const result = await pb.collection('contracts').getList<Contract>(page, perPage, {
        filter,
        expand: 'tender,supplier',
        sort: '-sign_date',
      })
      return {
        ...result,
        items: result.items.map((item) => {
          const tenderExpand = (item.expand as any)?.tender
          const supplierExpand = (item.expand as any)?.supplier
          return {
            ...item,
            contract_number: (item as any).contract_number ?? (item as any).contract_no,
            total_price: (item as any).total_price ?? (item as any).value_amount_satang,
            tender_id: (item as any).tender,
            company_id: (item as any).supplier,
            expand: {
              ...item.expand,
              tender_id: tenderExpand
                ? {
                    ...tenderExpand,
                    project_name:
                      tenderExpand.project_name ||
                      tenderExpand.title_th ||
                      '',
                  }
                : undefined,
              company_id: supplierExpand
                ? {
                    ...supplierExpand,
                    name:
                      supplierExpand.name ||
                      supplierExpand.name_th ||
                      supplierExpand.name_en ||
                      '',
                  }
                : undefined,
            },
          }
        }),
      }
    },
  })
}

export function useContract(contractId: string) {
  return useQuery({
    queryKey: ['contract', contractId],
    queryFn: async () => {
      const contract = await pb.collection('contracts').getOne<Contract>(contractId, {
        expand: 'tender,supplier',
      })
      return contract
    },
    enabled: !!contractId,
  })
}

// Awards
export function useAwards(filters?: {
  search?: string
  page?: number
  perPage?: number
}) {
  const { page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['awards', filters],
    queryFn: async () => {
      const result = await pb.collection('awards').getList<Award>(page, perPage, {
        expand: 'tender,supplier',
        sort: '-award_date',
      })
      return {
        ...result,
        items: result.items.map((item) => {
          const tenderExpand = (item.expand as any)?.tender
          const supplierExpand = (item.expand as any)?.supplier
          return {
            ...item,
            total_price: (item as any).total_price ?? (item as any).awarded_amount_satang,
            tender_id: (item as any).tender,
            company_id: (item as any).supplier,
            expand: {
              ...item.expand,
              tender_id: tenderExpand
                ? {
                    ...tenderExpand,
                    project_name:
                      tenderExpand.project_name ||
                      tenderExpand.title_th ||
                      '',
                  }
                : undefined,
              company_id: supplierExpand
                ? {
                    ...supplierExpand,
                    name:
                      supplierExpand.name ||
                      supplierExpand.name_th ||
                      supplierExpand.name_en ||
                      '',
                  }
                : undefined,
            },
          }
        }),
      }
    },
  })
}

// Bids
export function useBids(filters?: {
  page?: number
  perPage?: number
}) {
  const { page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['bids', filters],
    queryFn: async () => {
      const result = await pb.collection('bids').getList<Bid>(page, perPage, {
        expand: 'tender,supplier',
        sort: '-submitted_at',
      })
      return {
        ...result,
        items: result.items.map((item) => {
          const tenderExpand = (item.expand as any)?.tender
          const supplierExpand = (item.expand as any)?.supplier
          return {
            ...item,
            bid_price: (item as any).bid_price ?? (item as any).bid_amount_satang,
            tender_id: (item as any).tender,
            company_id: (item as any).supplier,
            expand: {
              ...item.expand,
              tender_id: tenderExpand
                ? {
                    ...tenderExpand,
                    project_name:
                      tenderExpand.project_name ||
                      tenderExpand.title_th ||
                      '',
                  }
                : undefined,
              company_id: supplierExpand
                ? {
                    ...supplierExpand,
                    name:
                      supplierExpand.name ||
                      supplierExpand.name_th ||
                      supplierExpand.name_en ||
                      '',
                  }
                : undefined,
            },
          }
        }),
      }
    },
  })
}

// Crawl Runs
export function useCrawlRuns(filters?: {
  page?: number
  perPage?: number
}) {
  const { page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['crawl-runs', filters],
    queryFn: async () => {
      const result = await pb.collection('crawl_runs').getList<CrawlRun>(page, perPage, {
        sort: '-started_at',
      })
      return result
    },
  })
}

// OCR Tasks
export function useOcrTasks(filters?: {
  status?: string
  page?: number
  perPage?: number
}) {
  const { status, page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['ocr-tasks', filters],
    queryFn: async () => {
      let filter = ''
      if (status) {
        filter = `status = "${status}"`
      }

      const result = await pb.collection('ocr_tasks').getList<OcrTask>(page, perPage, {
        filter,
        expand: 'attachment',
      })
      return {
        ...result,
        items: result.items.map((item) => ({
          ...item,
          attachment_id: (item as any).attachment,
          expand: { ...item.expand, attachment_id: (item.expand as any)?.attachment },
        })),
      }
    },
  })
}

// OCR Results
export function useOcrResults(filters?: {
  page?: number
  perPage?: number
}) {
  const { page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['ocr-results', filters],
    queryFn: async () => {
      const result = await pb.collection('ocr_results').getList<OcrResult>(page, perPage, {
        expand: 'attachment',
      })
      return {
        ...result,
        items: result.items.map((item) => ({
          ...item,
          attachment_id: (item as any).attachment,
          expand: { ...item.expand, attachment_id: (item.expand as any)?.attachment },
        })),
      }
    },
  })
}

// Attachments
export function useAttachments(filters?: {
  search?: string
  page?: number
  perPage?: number
}) {
  const { search, page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['attachments', filters],
    queryFn: async () => {
      let filter = ''
      if (search) {
        filter = `file ~ "${search}"`
      }

      const result = await pb.collection('attachments').getList<Attachment>(page, perPage, {
        filter,
        expand: 'tender',
        sort: '-published_at',
      })
      return {
        ...result,
        items: result.items.map((item) => ({
          ...item,
          tender_id: (item as any).tender,
          expand: { ...(item as any).expand, tender_id: (item as any).expand?.tender },
        })),
      }
    },
  })
}
